/**
 * CR126: Header Typography Presets
 * 
 * Defines typography presets for the Deal Finder header.
 * Presets map to Theme Labs tokens and provide consistent styling options.
 */

import { resolveFontFamily, DEFAULT_HEADING_FONT_ID, DEFAULT_BODY_FONT_ID } from "@/lib/theme/fontRegistry";
import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";

// ================================================================
// TYPES
// ================================================================

export type HeaderTypographyPresetId =
  | "default"
  | "hero_bold"
  | "minimal"
  | "compact";

export interface HeaderTypographyConfig {
  /** Selected preset ID */
  presetId: HeaderTypographyPresetId;
  /** Optional override for title token (from Theme Labs) */
  titleTokenId?: string;
  /** Optional override for subtitle token (from Theme Labs) */
  subtitleTokenId?: string;
}

export interface ResolvedHeaderTypography {
  titleStyle: React.CSSProperties;
  subtitleStyle: React.CSSProperties;
}

// ================================================================
// PRESET DEFINITIONS
// ================================================================

export interface TypographyPresetDefinition {
  id: HeaderTypographyPresetId;
  label: string;
  description: string;
  /** Title styling relative to theme tokens */
  title: {
    fontWeight: number | string;
    fontSize: string;
    lineHeight: string;
    letterSpacing?: string;
    textTransform?: "none" | "uppercase" | "lowercase" | "capitalize";
  };
  /** Subtitle styling relative to theme tokens */
  subtitle: {
    fontWeight: number | string;
    fontSize: string;
    lineHeight: string;
    letterSpacing?: string;
    opacity?: number;
  };
}

export const HEADER_TYPOGRAPHY_PRESETS: TypographyPresetDefinition[] = [
  {
    id: "default",
    label: "Default (Follow theme)",
    description: "Uses standard theme typography",
    title: {
      fontWeight: 700,
      fontSize: "1.25rem", // text-xl
      lineHeight: "1.75rem",
    },
    subtitle: {
      fontWeight: 400,
      fontSize: "0.875rem", // text-sm
      lineHeight: "1.25rem",
    },
  },
  {
    id: "hero_bold",
    label: "Hero Bold",
    description: "Large, impactful header for promotional pages",
    title: {
      fontWeight: 800,
      fontSize: "2rem", // text-3xl
      lineHeight: "2.25rem",
      letterSpacing: "-0.025em",
    },
    subtitle: {
      fontWeight: 500,
      fontSize: "1.125rem", // text-lg
      lineHeight: "1.75rem",
    },
  },
  {
    id: "minimal",
    label: "Minimal",
    description: "Light, refined typography for understated designs",
    title: {
      fontWeight: 500,
      fontSize: "1rem", // text-base
      lineHeight: "1.5rem",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },
    subtitle: {
      fontWeight: 400,
      fontSize: "0.75rem", // text-xs
      lineHeight: "1rem",
      opacity: 0.7,
    },
  },
  {
    id: "compact",
    label: "Compact",
    description: "Space-efficient typography for dense layouts",
    title: {
      fontWeight: 600,
      fontSize: "1rem", // text-base
      lineHeight: "1.25rem",
    },
    subtitle: {
      fontWeight: 400,
      fontSize: "0.75rem", // text-xs
      lineHeight: "1rem",
    },
  },
];

// ================================================================
// DEFAULT CONFIG
// ================================================================

export const DEFAULT_HEADER_TYPOGRAPHY: HeaderTypographyConfig = {
  presetId: "default",
  titleTokenId: undefined,
  subtitleTokenId: undefined,
};

// ================================================================
// RESOLVER FUNCTION
// ================================================================

/**
 * Resolves header typography config into concrete CSS styles.
 * 
 * @param config - The header typography configuration
 * @param themeTokens - Optional Theme Labs tokens for font resolution
 * @param layoutTypography - Optional Layout Studio typography config
 * @returns Resolved title and subtitle styles
 */
export function resolveHeaderTypography(
  config: HeaderTypographyConfig | undefined,
  themeTokens?: GlobalThemeTokens | null,
  layoutTypography?: { titleFontId?: string; uiFontId?: string }
): ResolvedHeaderTypography {
  // Get effective preset
  const presetId = config?.presetId || "default";
  const preset = HEADER_TYPOGRAPHY_PRESETS.find((p) => p.id === presetId) 
    || HEADER_TYPOGRAPHY_PRESETS[0]!;

  // Resolve font families from multiple sources (priority order):
  // 1. config.titleTokenId / subtitleTokenId (direct override)
  // 2. layoutTypography.titleFontId / uiFontId (Layout Studio)
  // 3. themeTokens.typography.headingFontId / bodyFontId (Theme Labs)
  // 4. Fallback to defaults
  
  const titleFontId = 
    config?.titleTokenId ||
    layoutTypography?.titleFontId ||
    themeTokens?.typography?.headingFontId ||
    DEFAULT_HEADING_FONT_ID;
    
  const subtitleFontId = 
    config?.subtitleTokenId ||
    layoutTypography?.uiFontId ||
    themeTokens?.typography?.bodyFontId ||
    DEFAULT_BODY_FONT_ID;

  const titleFontFamily = resolveFontFamily(titleFontId, DEFAULT_HEADING_FONT_ID);
  const subtitleFontFamily = resolveFontFamily(subtitleFontId, DEFAULT_BODY_FONT_ID);

  // Build title style
  const titleStyle: React.CSSProperties = {
    fontFamily: titleFontFamily,
    fontWeight: preset.title.fontWeight,
    fontSize: preset.title.fontSize,
    lineHeight: preset.title.lineHeight,
  };
  
  if (preset.title.letterSpacing) {
    titleStyle.letterSpacing = preset.title.letterSpacing;
  }
  if (preset.title.textTransform) {
    titleStyle.textTransform = preset.title.textTransform;
  }

  // Build subtitle style
  const subtitleStyle: React.CSSProperties = {
    fontFamily: subtitleFontFamily,
    fontWeight: preset.subtitle.fontWeight,
    fontSize: preset.subtitle.fontSize,
    lineHeight: preset.subtitle.lineHeight,
  };
  
  if (preset.subtitle.letterSpacing) {
    subtitleStyle.letterSpacing = preset.subtitle.letterSpacing;
  }
  if (preset.subtitle.opacity !== undefined) {
    subtitleStyle.opacity = preset.subtitle.opacity;
  }

  return { titleStyle, subtitleStyle };
}

/**
 * Get a preset by ID with fallback to default.
 */
export function getHeaderTypographyPreset(presetId?: HeaderTypographyPresetId): TypographyPresetDefinition {
  if (!presetId) return HEADER_TYPOGRAPHY_PRESETS[0]!;
  return HEADER_TYPOGRAPHY_PRESETS.find((p) => p.id === presetId) || HEADER_TYPOGRAPHY_PRESETS[0]!;
}
